import type { AreaResponse } from '@/src/lib/api';

export type HomepageHeroId = 'sf' | 'la' | 'miami' | 'dc' | 'seattle' | 'chicago';
export type HomepageHeroSearchParams = Record<string, string | Array<string> | undefined>;
export type HomepageHeroTheme = 'day' | 'night';
export type HomepageHeroViewport = 'mobile' | 'tablet' | 'desktop';

type CoordinateBounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

type HomepageHeroMatchRule = {
  resourceIds?: ReadonlyArray<string>;
  states?: ReadonlyArray<string>;
  stateFallbacks?: ReadonlyArray<string>;
  counties?: ReadonlyArray<string>;
  bounds?: ReadonlyArray<CoordinateBounds>;
};

export type HomepageHero = {
  id: HomepageHeroId;
  label: string;
  previewParam: string;
  match: HomepageHeroMatchRule;
};

export type HomepageHeroConfig = Pick<HomepageHero, 'id' | 'label'>;

const BAY_AREA_COUNTIES = [
  'alameda',
  'contra costa',
  'marin',
  'napa',
  'san francisco',
  'san mateo',
  'santa clara',
  'solano',
  'sonoma',
] as const;

const SOUTHERN_CALIFORNIA_COUNTIES = [
  'imperial',
  'los angeles',
  'orange',
  'riverside',
  'san bernardino',
  'san diego',
  'santa barbara',
  'ventura',
] as const;

const SOUTH_FLORIDA_COUNTIES = ['broward', 'miami-dade', 'monroe', 'palm beach'] as const;
const PUGET_SOUND_COUNTIES = ['king', 'kitsap', 'pierce', 'snohomish'] as const;
const CHICAGO_AREA_COUNTIES = ['cook', 'dupage', 'kane', 'lake', 'mchenry', 'will'] as const;

const DC_METRO_COUNTIES = [
  'alexandria city',
  'arlington',
  'district of columbia',
  'fairfax',
  'montgomery',
  'prince george s',
] as const;

export const DEFAULT_HOMEPAGE_HERO_ID: HomepageHeroId = 'sf';

// Add future homepage locales here: city `id`, label, and rough geo match rules.
// Artwork paths are derived from the `id` inside HomepageHero.
export const HOMEPAGE_HEROES = {
  sf: {
    id: 'sf',
    label: 'San Francisco',
    previewParam: '1',
    match: {
      resourceIds: [
        'berkeley-ca',
        'daly-city-ca',
        'fremont-ca',
        'hayward-ca',
        'mountain-view-ca',
        'oakland-ca',
        'palo-alto-ca',
        'san-francisco-ca',
        'san-jose-ca',
        'san-mateo-ca',
        'san-rafael-ca',
        'santa-clara-ca',
        'sunnyvale-ca',
        'walnut-creek-ca',
      ],
      states: ['CA'],
      counties: BAY_AREA_COUNTIES,
      bounds: [
        {
          minLat: 36.7,
          maxLat: 38.65,
          minLon: -123.25,
          maxLon: -121.55,
        },
      ],
    },
  },
  la: {
    id: 'la',
    label: 'Los Angeles',
    previewParam: '2',
    match: {
      resourceIds: [
        'anaheim-ca',
        'burbank-ca',
        'glendale-ca',
        'irvine-ca',
        'long-beach-ca',
        'los-angeles-ca',
        'pasadena-ca',
        'riverside-ca',
        'san-bernardino-ca',
        'san-diego-ca',
        'santa-ana-ca',
        'santa-barbara-ca',
        'santa-monica-ca',
        'ventura-ca',
      ],
      states: ['CA'],
      counties: SOUTHERN_CALIFORNIA_COUNTIES,
      bounds: [
        {
          minLat: 32.4,
          maxLat: 35.9,
          minLon: -120.75,
          maxLon: -114,
        },
      ],
    },
  },
  miami: {
    id: 'miami',
    label: 'Miami',
    previewParam: '3',
    match: {
      resourceIds: [
        'aventura-fl',
        'boca-raton-fl',
        'coral-gables-fl',
        'doral-fl',
        'fort-lauderdale-fl',
        'hialeah-fl',
        'hollywood-fl',
        'key-biscayne-fl',
        'kendall-fl',
        'miami-beach-fl',
        'miami-fl',
        'north-miami-fl',
        'pompano-beach-fl',
        'west-palm-beach-fl',
      ],
      states: ['FL'],
      counties: SOUTH_FLORIDA_COUNTIES,
      bounds: [
        {
          minLat: 25.1,
          maxLat: 27.1,
          minLon: -81.9,
          maxLon: -79.75,
        },
      ],
    },
  },
  dc: {
    id: 'dc',
    label: 'Washington, D.C.',
    previewParam: '4',
    match: {
      resourceIds: [
        'alexandria-va',
        'arlington-va',
        'bethesda-md',
        'college-park-md',
        'fairfax-va',
        'falls-church-va',
        'gaithersburg-md',
        'hyattsville-md',
        'mclean-va',
        'reston-va',
        'rockville-md',
        'silver-spring-md',
        'washington-dc',
      ],
      states: ['DC', 'MD', 'VA'],
      stateFallbacks: ['DC'],
      counties: DC_METRO_COUNTIES,
      bounds: [
        {
          minLat: 38.65,
          maxLat: 39.25,
          minLon: -77.55,
          maxLon: -76.7,
        },
      ],
    },
  },
  seattle: {
    id: 'seattle',
    label: 'Seattle',
    previewParam: '5',
    match: {
      resourceIds: [
        'bellevue-wa',
        'bothell-wa',
        'burien-wa',
        'edmonds-wa',
        'everett-wa',
        'kent-wa',
        'kirkland-wa',
        'lynnwood-wa',
        'redmond-wa',
        'renton-wa',
        'seattle-wa',
        'shoreline-wa',
        'tacoma-wa',
      ],
      states: ['WA'],
      counties: PUGET_SOUND_COUNTIES,
      bounds: [
        {
          minLat: 47.05,
          maxLat: 48.15,
          minLon: -123,
          maxLon: -121.65,
        },
      ],
    },
  },
  chicago: {
    id: 'chicago',
    label: 'Chicago',
    previewParam: '6',
    match: {
      resourceIds: [
        'arlington-heights-il',
        'berwyn-il',
        'chicago-il',
        'cicero-il',
        'des-plaines-il',
        'elgin-il',
        'evanston-il',
        'joliet-il',
        'naperville-il',
        'oak-park-il',
        'orland-park-il',
        'schaumburg-il',
        'skokie-il',
        'waukegan-il',
      ],
      states: ['IL'],
      counties: CHICAGO_AREA_COUNTIES,
      bounds: [
        {
          minLat: 41.35,
          maxLat: 42.55,
          minLon: -88.55,
          maxLon: -87.4,
        },
      ],
    },
  },
} as const satisfies Record<HomepageHeroId, HomepageHero>;

const HOMEPAGE_HERO_MATCH_ORDER: ReadonlyArray<HomepageHeroId> = [
  'sf',
  'la',
  'miami',
  'dc',
  'seattle',
  'chicago',
];

function normalizeResourceId(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

function normalizeState(value: string | null | undefined): string {
  return value?.trim().toUpperCase() ?? '';
}

function normalizeLocationName(value: string | null | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/\bcounty\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function getAreaPoint(area: AreaResponse): { lat: number; lon: number } | null {
  if (isFiniteNumber(area.coordinates?.lat) && isFiniteNumber(area.coordinates.lon)) {
    return area.coordinates;
  }

  if (
    isFiniteNumber(area.minLat) &&
    isFiniteNumber(area.maxLat) &&
    isFiniteNumber(area.minLon) &&
    isFiniteNumber(area.maxLon)
  ) {
    return {
      lat: (area.minLat + area.maxLat) / 2,
      lon: (area.minLon + area.maxLon) / 2,
    };
  }

  return null;
}

function boundsContainArea(area: AreaResponse, bounds: ReadonlyArray<CoordinateBounds> | undefined): boolean {
  if (!bounds?.length) return false;

  const point = getAreaPoint(area);
  if (!point) return false;

  return bounds.some(
    (box) =>
      point.lat >= box.minLat &&
      point.lat <= box.maxLat &&
      point.lon >= box.minLon &&
      point.lon <= box.maxLon,
  );
}

function matchesCounty(area: AreaResponse, match: HomepageHeroMatchRule): boolean {
  if (!match.counties?.length) return false;

  const areaState = normalizeState(area.state);
  const areaCounty = normalizeLocationName(area.county);
  if (!areaCounty) return false;

  const stateMatches = !match.states?.length || match.states.includes(areaState);
  if (!stateMatches) return false;

  return match.counties.some((county) => normalizeLocationName(county) === areaCounty);
}

function matchesState(area: AreaResponse, match: HomepageHeroMatchRule): boolean {
  const candidateStates = match.counties?.length ? match.stateFallbacks : match.states;
  if (!candidateStates?.length) return false;
  return candidateStates.includes(normalizeState(area.state));
}

function matchesResourceId(area: AreaResponse, match: HomepageHeroMatchRule): boolean {
  if (!match.resourceIds?.length) return false;

  const resourceId = normalizeResourceId(area.resourceId);
  if (!resourceId) return false;

  return match.resourceIds.some((candidate) => normalizeResourceId(candidate) === resourceId);
}

function matchesHero(area: AreaResponse, hero: HomepageHero): boolean {
  return (
    matchesResourceId(area, hero.match) ||
    boundsContainArea(area, hero.match.bounds) ||
    matchesCounty(area, hero.match) ||
    matchesState(area, hero.match)
  );
}

export function selectMatchingHomepageHeroId(area: AreaResponse | null | undefined): HomepageHeroId | null {
  if (!area) return null;

  return HOMEPAGE_HERO_MATCH_ORDER.find((heroId) => matchesHero(area, HOMEPAGE_HEROES[heroId])) ?? null;
}

export function selectHomepageHeroId(area: AreaResponse | null | undefined): HomepageHeroId {
  return selectMatchingHomepageHeroId(area) ?? DEFAULT_HOMEPAGE_HERO_ID;
}

export function getHomepageHeroQueryParamOverride(
  searchParams: HomepageHeroSearchParams,
): HomepageHeroId | null {
  const value = searchParams.h;
  const heroValue = Array.isArray(value) ? value[0] : value;
  if (!heroValue) return null;

  return Object.values(HOMEPAGE_HEROES).find((hero) => hero.previewParam === heroValue)?.id ?? null;
}

export function getHomepageHero(heroId: HomepageHeroId): HomepageHero {
  return HOMEPAGE_HEROES[heroId] ?? HOMEPAGE_HEROES[DEFAULT_HOMEPAGE_HERO_ID];
}

export function getHomepageHeroConfig(heroId: HomepageHeroId): HomepageHeroConfig {
  const { id, label } = getHomepageHero(heroId);
  return { id, label };
}
